import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {
  ProcedureParameterConfirmationMethod,
  ProcedureRunDataEntryMode,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import type {TypedValue} from 'notebook/tables/TableConstants';
import {ExecutionPlan} from 'studies/progress/Types';
import {patchJSONPromise, postJSON, postJSONPromise} from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export interface ProcedureRunPlanReplicateInfoREST {
  replicateNumber: number;
  sourcePMEIIdsByTemplatePMDVId?: Record<string, Array<string>>;
}

export interface PlanDataParameterValueItemREST {
  value: TypedValue | null;
}
export interface PlanMaterialInputValueItemREST {
  value: {
    material: TypedValue | null;
    amount: TypedValue | null;
  };
}
export interface PlanEquipmentValueItemREST {
  value: {
    equipment: TypedValue | null;
  };
}

export interface PlanDataParameterValueREST {
  dataParameterVersionId: string;
  values: Array<PlanDataParameterValueItemREST>;
}

export interface PlanMaterialInputValueREST {
  materialInputVersionId: string;
  values: Array<PlanMaterialInputValueItemREST>;
}

export interface PlanEquipmentValueREST {
  equipmentVersionId: string;
  values: Array<PlanEquipmentValueItemREST>;
}

export interface PlanMethodPlanREST {
  methodVersionId: string;
  dataParameterValues: Array<PlanDataParameterValueREST>;
  materialInputValues: Array<PlanMaterialInputValueREST>;
  equipmentValues: Array<PlanEquipmentValueREST>;
}

export interface ProcedureRunPlanConditionREST {
  id?: string; // Optional for new conditions
  name: string;
  description?: string;
  position: number;
  methodPlans: Array<PlanMethodPlanREST>;
  replicateInfos: Array<ProcedureRunPlanReplicateInfoREST>;
}

export interface UpdateProcedureRunPlanParams {
  id: string;
  studyId: string;
  description?: string;
  experimentConditions?: Array<ProcedureRunPlanConditionREST>;
  confirmationMethodByParamVersionId?: Record<string, ProcedureParameterConfirmationMethod>;
  dataEntryMode?: ProcedureRunDataEntryMode;
  templateProcedureFlowchartVersionId?: string;
  workflowFlowchartConfigVersionId?: string;
}

export interface UpdateProcedureRunPlanResponse {
  updatedIds: Array<string>;
}

export interface CreateProcedureRunPlansParams {
  description: string;
  creatorId: string;
  dataEntryMode: ProcedureRunDataEntryMode;
  workflowFlowchartConfigVersionId: string;
}

export interface CreateProcedureRunPlansResponse {
  createdIds: Array<string>;
}

export interface BulkCreateProcedureRunPlansParam {
  creates: Array<CreateProcedureRunPlansParams>;
}

export function createProcedureRunPlans(
  studyId: string,
  creates: Array<CreateProcedureRunPlansParams>
): Promise<CreateProcedureRunPlansResponse> {
  // Filter out null values to satisfy backend XOR validation for
  // templateProcedureFlowchartVersionId and workflowFlowchartConfigVersionId
  const createsWithoutNulls = creates.map((create) =>
    Object.fromEntries(Object.entries(create).filter(([_, value]) => value != null))
  );

  return postJSONPromise({
    url: `${getApiBaseForObjectType(DomainGraphType.STUDY)}/${studyId}:procedure-run-plans`,
    data: {creates: createsWithoutNulls},
  }) as Promise<CreateProcedureRunPlansResponse>;
}

export async function updateProcedureRunPlan(
  plan: UpdateProcedureRunPlanParams
): Promise<UpdateProcedureRunPlanResponse> {
  const {studyId, ...updateParams} = plan;
  const updates = [updateParams];
  const url = `${getApiBaseForObjectType(DomainGraphType.STUDY)}/${studyId}:procedure-run-plans`;

  const response = await patchJSONPromise({
    url,
    data: {updates},
  });
  return response as UpdateProcedureRunPlanResponse;
}

export interface PromoteProcedureRunPlansResponse {
  // array of created procedure run ids and their respective folder ids.
  creates: Array<{id: string; folderId: string}>;
}

export interface PromoteProcedureRunPlansTaskResponse extends TaskRunner.OngoingTaskResponse {
  taskId: string;
  timingMeta: ExecutionPlan;
}

export async function promoteProcedureRunPlans(
  procedureRunPlanIds: Array<string>,
  {supportsAsync}: {supportsAsync: boolean}
): Promise<
  | {async: false; data: PromoteProcedureRunPlansResponse}
  | {async: true; data: PromoteProcedureRunPlansTaskResponse; taskMeta: TaskRunner.TaskMeta}
> {
  const url = `${getApiBaseForObjectType(DomainGraphType.PROCEDURE_RUN_PLAN)}:promote?supportsAsync=${
    supportsAsync ? 1 : 0
  }`;
  // We use legacy postJSON because net.postJSONPromise / TaskRunner.runTask are too limited: we need to
  // inspect the response status code / headers since our response is dynamic and may either be a
  // synchronous web request or an asynchronous celery task, where our UX will reflect the asynchrony.
  const xhr: JQueryXHR = postJSON({
    url,
    data: {procedureRunPlanIds},
  }) as JQueryXHR;
  const response = await Promise.resolve(xhr);
  if (xhr.status === 202) {
    return {
      async: true,
      data: response as PromoteProcedureRunPlansTaskResponse,
      taskMeta: TaskRunner.getTaskMeta(xhr, url),
    };
  }
  return {
    async: false,
    data: response as PromoteProcedureRunPlansResponse,
  };
}
